import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Grid, 
  Button, 
  Typography, 
  CircularProgress, 
  LinearProgress, 
  Divider, 
  Alert,
  Snackbar,
  Chip,
  Tooltip
} from '@mui/material';
import MonacoEditor from 'react-monaco-editor';
import { submitCode, getResult } from '../services/judge0Service';
import { 
  generateCodingChallenge, 
  evaluateCode, 
  isApiKeyConfigured 
} from '../services/aiService';
import TimerIcon from '@mui/icons-material/Timer';
import CodeIcon from '@mui/icons-material/Code';
import BugReportIcon from '@mui/icons-material/BugReport';
import BuildIcon from '@mui/icons-material/Build';
import DifficultySelector from './DifficultySelector';
import LanguageSelector, { getJudgeIdForLanguage } from './LanguageSelector';
import LockIcon from '@mui/icons-material/Lock';
import PsychologyIcon from '@mui/icons-material/Psychology';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

// 倒计时初始时间（秒）
const DEFAULT_COUNTDOWN = 120;

// 分析代码差异的函数
const analyzeDiff = (originalCode, modifiedCode) => {
  // 简单版本，查找修改的行
  const originalLines = originalCode.split('\n');
  const modifiedLines = modifiedCode.split('\n');
  
  const changes = [];
  
  for (let i = 0; i < Math.max(originalLines.length, modifiedLines.length); i++) {
    const origLine = i < originalLines.length ? originalLines[i] : '';
    const modLine = i < modifiedLines.length ? modifiedLines[i] : '';
    
    if (origLine !== modLine) {
      changes.push({
        lineNumber: i + 1,
        original: origLine,
        modified: modLine
      });
    }
  }
  
  return changes;
};

function CodeBattle() {
  // 基本状态
  const [buggyCode, setBuggyCode] = useState('');
  const [fixedCode, setFixedCode] = useState('');
  const [userCode, setUserCode] = useState('');
  const [aiCode, setAiCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [results, setResults] = useState(null);
  const [countdown, setCountdown] = useState(DEFAULT_COUNTDOWN);
  const [timerActive, setTimerActive] = useState(false);
  const [battleStarted, setBattleStarted] = useState(false);
  const [userChanges, setUserChanges] = useState([]);
  const [aiChanges, setAiChanges] = useState([]);
  const [difficulty, setDifficulty] = useState('medium');
  const [language, setLanguage] = useState('c');
  const [bugDescription, setBugDescription] = useState('');
  const [hint, setHint] = useState('');
  const [showHint, setShowHint] = useState(false);
  const [apiKeyError, setApiKeyError] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [previewMode, setPreviewMode] = useState('blurred'); // 'blurred', 'hinted', 'hidden'

  // 获取当前选择语言的Judge0 ID
  const getCurrentLanguageId = () => getJudgeIdForLanguage(language);
  
  // 语言选择改变时的处理函数
  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    // 如果改变语言，也要刷新挑战
    if (newLanguage !== language && !battleStarted) {
      setTimeout(() => generateChallenge(difficulty, newLanguage), 300);
    }
  };

  // 倒计时效果
  useEffect(() => {
    let timer = null;
    
    if (timerActive && countdown > 0) {
      timer = setInterval(() => {
        setCountdown(seconds => seconds - 1);
      }, 1000);
    } else if (countdown === 0 && battleStarted) {
      // 时间到，自动提交
      handleSubmit();
    }
    
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [timerActive, countdown, battleStarted]);

  // 生成新的代码挑战，传入语言参数
  const generateChallenge = async (difficultyLevel = difficulty, languageChoice = language) => {
    // 检查API密钥是否已配置
    if (!isApiKeyConfigured()) {
      setApiKeyError(true);
      return;
    }
    
    setGenerating(true);
    try {
      const challenge = await generateCodingChallenge(difficultyLevel, languageChoice);
      
      setBuggyCode(challenge.buggyCode);
      setFixedCode(challenge.fixedCode);
      setUserCode(challenge.buggyCode);
      setAiCode(challenge.buggyCode);
      setBugDescription(challenge.bugDescription);
      setHint(challenge.hint || '');
      
      setUserChanges([]);
      setAiChanges([]);
    } catch (error) {
      console.error('生成挑战失败:', error);
    } finally {
      setGenerating(false);
    }
  };

  // 页面加载时生成默认挑战
  useEffect(() => {
    // 根据当前语言设置默认代码
    const getDefaultCodeForLanguage = (lang) => {
      switch(lang) {
        case 'python':
          return '# 这里有一个bug\ndef calculate_sum():\n    total = 0\n    for i in range(1, 6)\n        total += i\n    return total\n\nprint(calculate_sum())';
        case 'javascript':
          return '// 这里有一个bug\nfunction calculateSum() {\n    let total = 0;\n    for (let i = 1; i <= 5 i++) {\n        total += i;\n    }\n    return total;\n}\n\nconsole.log(calculateSum());';
        case 'java':
          return 'public class Main {\n    public static void main(String[] args) {\n        // 这里有一个bug\n        System.out.println("Sum: " + calculateSum())\n    }\n    \n    public static int calculateSum() {\n        int total = 0;\n        for (int i = 1; i <= 5; i++) {\n            total += i;\n        }\n        return total;\n    }\n}';
        case 'cpp':
          return '#include <iostream>\n\nint main() {\n    // 这里有一个bug\n    int sum = 0\n    for (int i = 1; i <= 5; i++) {\n        sum += i;\n    }\n    std::cout << "Sum: " << sum << std::endl;\n    return 0;\n}';
        case 'c':
        default:
          return '#include <stdio.h>\n\nint main() {\n    // 这里有一个bug\n    int a = 10\n    printf("a = %d\\n", a);\n    return 0;\n}';
      }
    };

    // 设置默认代码，以防API调用失败
    const defaultBuggyCode = getDefaultCodeForLanguage(language);
    setBuggyCode(defaultBuggyCode);
    setUserCode(defaultBuggyCode);
    setAiCode(defaultBuggyCode);
    setBugDescription("缺少分号或冒号");
    
    // 尝试生成一个新挑战
    if (isApiKeyConfigured()) {
      generateChallenge();
    }
  }, []);

  const handleUserCodeChange = (value) => {
    setUserCode(value);
    // 更新用户修改内容
    const changes = analyzeDiff(buggyCode, value);
    setUserChanges(changes);
  };

  const startBattle = () => {
    // 检查是否有代码
    if (!buggyCode) {
      setApiKeyError(true);
      return;
    }
    
    setBattleStarted(true);
    setTimerActive(true);
    setEvaluation(null);
    setResults(null);
    setShowHint(false);
    
    // AI修复（使用预先生成的修复代码，但先不展示给用户）
    // 此时不修改aiCode，保持和buggyCode一致，等提交后才设置为修复后的代码
    setAiCode(buggyCode);
    
    // 用户和AI的修改初始为空
    setUserChanges([]);
    setAiChanges([]);
  };

  // 获取提示
  const getHint = () => {
    setShowHint(true);
  };

  const handleSubmit = async () => {
    if (loading) return;
    
    setTimerActive(false);
    setLoading(true);
    
    try {
      const languageId = getCurrentLanguageId();
      
      // 提交用户代码
      const userSubmission = await submitCode(userCode, languageId);
      
      // 提交AI修复后的代码（使用fixedCode）
      const aiSubmission = await submitCode(fixedCode, languageId);

      // 等待结果
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const userResult = await getResult(userSubmission.token);
      const aiResult = await getResult(aiSubmission.token);
      
      // 使用AI评估用户代码
      let userEvaluation = null;
      if (isApiKeyConfigured()) {
        userEvaluation = await evaluateCode(buggyCode, userCode, fixedCode);
      }
      
      setEvaluation(userEvaluation);
      
      // 在结果显示时，才展示AI成功修复的代码
      setAiCode(fixedCode);
      
      setResults({
        user: userResult,
        ai: aiResult,
        winner: userResult.status.id === 3 ? 'user' : 'ai',
        userTime: countdown > 0 ? DEFAULT_COUNTDOWN - countdown : DEFAULT_COUNTDOWN,
        bugDescription: bugDescription
      });
      
      // 最终分析代码差异
      setUserChanges(analyzeDiff(buggyCode, userCode));
      setAiChanges(analyzeDiff(buggyCode, fixedCode));
      
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
    setBattleStarted(false);
  };

  const resetBattle = () => {
    setUserCode(buggyCode);
    setAiCode(buggyCode);
    setResults(null);
    setEvaluation(null);
    setCountdown(DEFAULT_COUNTDOWN);
    setTimerActive(false);
    setBattleStarted(false);
    setUserChanges([]);
    setAiChanges([]);
    setShowHint(false);
  };

  const newChallenge = () => {
    resetBattle();
    generateChallenge(difficulty, language);
  };

  // 格式化倒计时显示
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // 计算时间进度条
  const timeProgress = ((DEFAULT_COUNTDOWN - countdown) / DEFAULT_COUNTDOWN) * 100;

  // 格式化显示代码差异
  const renderDiff = (changes) => {
    if (changes.length === 0) return <Typography color="text.secondary">没有修改</Typography>;
    
    return changes.map((change, index) => (
      <Box key={index} sx={{ my: 1, p: 1, bgcolor: 'rgba(0, 0, 0, 0.1)', borderRadius: 1 }}>
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          行 {change.lineNumber}: 
        </Typography>
        {change.original && (
          <Typography variant="body2" color="error" sx={{ fontFamily: 'monospace', textDecoration: 'line-through' }}>
            - {change.original}
          </Typography>
        )}
        {change.modified && (
          <Typography variant="body2" color="success.main" sx={{ fontFamily: 'monospace' }}>
            + {change.modified}
          </Typography>
        )}
      </Box>
    ));
  };

  // 处理切换预览模式
  const handleTogglePreview = () => {
    setPreviewMode(prev => {
      if (prev === 'blurred') return 'hinted';
      if (prev === 'hinted') return 'hidden';
      return 'blurred';
    });
  };

  // 代码模糊化处理函数
  const getBlurredCode = (code) => {
    if (!code) return '';
    
    if (previewMode === 'hidden') {
      return '/* 代码已隐藏，开始挑战后可查看 */';
    }
    
    if (previewMode === 'hinted') {
      // 只显示结构和注释，其他用占位符替代
      return code.split('\n').map(line => {
        // 保留注释、函数定义开头和结构
        if (line.trim().startsWith('//') || line.trim().startsWith('/*') || line.trim().startsWith('*/') || line.trim().startsWith('#')) {
          return line;
        } else if (line.includes('{') || line.includes('}') || line.includes('def ') || line.includes('class ') || line.includes('import ') || line.includes('include')) {
          return line;
        } else if (line.trim() === '') {
          return line;
        } else {
          // 将其他代码行替换为模糊提示
          const indentation = line.match(/^\s*/)[0];
          return `${indentation}... /* 代码未显示 */ ...`;
        }
      }).join('\n');
    }
    
    // blurred模式 - 使用CSS blur效果，在MonacoEditor外层添加
    return code;
  };

  return (
    <Box>
      <Snackbar
        open={apiKeyError}
        autoHideDuration={6000}
        onClose={() => setApiKeyError(false)}
        message="请先配置API密钥"
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />

      {!battleStarted && !results ? (
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
            发现并修复代码中的错误，与AI一决高下！
          </Typography>
          
          <Box sx={{ mt: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
              <Box sx={{ minWidth: 200 }}>
                <DifficultySelector 
                  difficulty={difficulty} 
                  onDifficultyChange={setDifficulty} 
                />
              </Box>
              
              <Box sx={{ minWidth: 200 }}>
                <LanguageSelector
                  language={language}
                  onLanguageChange={handleLanguageChange}
                />
              </Box>
            </Box>
            
            <Button
              variant="outlined"
              color="primary"
              onClick={() => generateChallenge(difficulty, language)}
              disabled={generating}
              sx={{ mr: 2 }}
            >
              {generating ? <CircularProgress size={24} /> : '生成新挑战'}
            </Button>
            
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={startBattle}
              disabled={generating || !buggyCode}
            >
              开始挑战
            </Button>
          </Box>
          
          {buggyCode && (
            <Paper elevation={3} sx={{ p: 2, maxWidth: 800, mx: 'auto', textAlign: 'left' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1">
                  挑战预览:
                </Typography>
                <Tooltip title="切换预览模式">
                  <Button 
                    startIcon={
                      previewMode === 'blurred' ? <VisibilityOffIcon /> : 
                      previewMode === 'hinted' ? <PsychologyIcon /> : 
                      <LockIcon />
                    }
                    onClick={handleTogglePreview}
                    size="small"
                    color="primary"
                    variant="outlined"
                  >
                    {previewMode === 'blurred' ? '模糊预览' : 
                     previewMode === 'hinted' ? '提示预览' : 
                     '完全隐藏'}
                  </Button>
                </Tooltip>
              </Box>
              
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                难度: {difficulty === 'easy' ? '简单' : (difficulty === 'medium' ? '中等' : '困难')}
                {' | '}
                语言: {language.toUpperCase()}
                {' | '}
                <Chip 
                  label="准备好了吗？点击开始挑战！" 
                  color="primary" 
                  size="small"
                  sx={{ ml: 1 }}
                />
              </Typography>
              
              <Box 
                sx={{ 
                  position: 'relative',
                  '&::after': previewMode === 'blurred' ? {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backdropFilter: 'blur(6px)',
                    zIndex: 1,
                    pointerEvents: 'none',
                    borderRadius: '4px'
                  } : {}
                }}
              >
                <MonacoEditor
                  height="200"
                  language={language === 'c' ? 'c' : (language === 'cpp' ? 'cpp' : language)}
                  theme="vs-dark"
                  value={getBlurredCode(buggyCode)}
                  options={{
                    readOnly: true,
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                  }}
                />
              </Box>
              
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Alert severity="info" icon={<PsychologyIcon />}>
                  <Typography variant="body2">
                    开始挑战后，你将看到完整代码并尝试修复其中的错误！
                  </Typography>
                </Alert>
              </Box>
            </Paper>
          )}
        </Box>
      ) : (
        <>
          {battleStarted && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TimerIcon color="warning" sx={{ mr: 1 }} />
              <Typography variant="h6" color={countdown < 30 ? "error" : "inherit"}>
                剩余时间: {formatTime(countdown)}
              </Typography>
              <Box sx={{ flexGrow: 1, ml: 2 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={timeProgress} 
                  color={countdown < 30 ? "error" : "primary"}
                  sx={{ height: 10, borderRadius: 5 }}
                />
              </Box>
            </Box>
          )}

          <Grid container spacing={3}>
            <Grid item xs={6}>
              <Paper elevation={3} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  你的代码
                </Typography>
                <MonacoEditor
                  height="400"
                  language={language === 'c' ? 'c' : (language === 'cpp' ? 'cpp' : language)}
                  theme="vs-dark"
                  value={userCode}
                  onChange={handleUserCodeChange}
                  options={{
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    readOnly: !battleStarted && results !== null,
                  }}
                />
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper elevation={3} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  AI的修复
                </Typography>
                <MonacoEditor
                  height="400"
                  language={language === 'c' ? 'c' : (language === 'cpp' ? 'cpp' : language)}
                  theme="vs-dark"
                  value={aiCode}
                  options={{
                    readOnly: true,
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                  }}
                />
              </Paper>
            </Grid>
          </Grid>

          {battleStarted && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              {hint && !showHint && (
                <Button
                  variant="outlined"
                  color="info"
                  onClick={getHint}
                  sx={{ mr: 2 }}
                >
                  获取提示
                </Button>
              )}
              
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : '提交代码'}
              </Button>
            </Box>
          )}
          
          {showHint && hint && (
            <Paper sx={{ mt: 3, p: 2, bgcolor: 'info.light', color: 'info.contrastText' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                提示: {hint}
              </Typography>
            </Paper>
          )}
        </>
      )}

      {results && (
        <Paper sx={{ mt: 3, p: 3, borderRadius: 2 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center' }}>
            对战结果
          </Typography>
          
          <Alert severity="info" icon={<BugReportIcon />} sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Bug详情: {results.bugDescription}
            </Typography>
          </Alert>
          
          {evaluation && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                AI评估
              </Typography>
              <Paper elevation={1} sx={{ p: 2, bgcolor: 'background.default' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ mr: 2 }}>
                    得分:
                  </Typography>
                  <Chip 
                    label={`${evaluation.score}/100`} 
                    color={evaluation.score > 80 ? "success" : (evaluation.score > 50 ? "warning" : "error")}
                  />
                </Box>
                <Typography variant="body1" gutterBottom>
                  <strong>反馈:</strong> {evaluation.feedback}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  <strong>详细分析:</strong> {evaluation.detailedAnalysis}
                </Typography>
              </Paper>
            </Box>
          )}
          
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <BuildIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">你的修复</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                <Typography variant="subtitle2" gutterBottom>
                  修改内容:
                </Typography>
                <Box sx={{ mb: 2, bgcolor: 'background.paper', p: 1, borderRadius: 1 }}>
                  {renderDiff(userChanges)}
                </Box>
                
                <Typography>
                  状态: {results.user.status.description}
                </Typography>
                {results.user.compile_output && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2">编译输出:</Typography>
                    <Typography sx={{ maxWidth: 400, whiteSpace: 'pre-wrap' }} color="error">
                      {results.user.compile_output}
                    </Typography>
                  </Box>
                )}
                <Typography>
                  用时: {results.userTime} 秒
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={6}>
              <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CodeIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">AI的修复</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                <Typography variant="subtitle2" gutterBottom>
                  修改内容:
                </Typography>
                <Box sx={{ mb: 2, bgcolor: 'background.paper', p: 1, borderRadius: 1 }}>
                  {renderDiff(aiChanges)}
                </Box>
                
                <Typography>
                  状态: {results.ai.status.description || "成功"}
                </Typography>
                
                {results.ai.compile_output ? (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2">编译输出:</Typography>
                    <Typography sx={{ maxWidth: 400, whiteSpace: 'pre-wrap' }} color="error">
                      {results.ai.compile_output}
                    </Typography>
                  </Box>
                ) : results.ai.stdout ? (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2">运行结果:</Typography>
                    <Typography sx={{ maxWidth: 400, whiteSpace: 'pre-wrap', fontFamily: 'monospace', bgcolor: 'rgba(0, 0, 0, 0.1)', p: 1, borderRadius: 1 }}>
                      {results.ai.stdout}
                    </Typography>
                  </Box>
                ) : null}
                
                <Typography>
                  用时: 2 秒
                </Typography>
              </Paper>
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ 
              color: results.winner === 'user' ? 'success.main' : 'error.main', 
              fontWeight: 'bold' 
            }}>
              {results.user.status.id === 3 
                ? '恭喜！你成功修复了代码！' 
                : 'AI修复了代码，再接再厉！'}
            </Typography>
            
            {results.user.status.id === 3 && results.ai.status.id === 3 && (
              <Typography sx={{ mt: 1 }}>
                {results.userTime < 2 
                  ? '你的修复速度比AI更快！' 
                  : 'AI的修复速度更快，但你也做得很好！'}
              </Typography>
            )}
            
            <Box sx={{ mt: 2 }}>
              <Button 
                variant="outlined" 
                color="primary" 
                onClick={resetBattle}
                sx={{ mr: 2 }}
              >
                再次挑战
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={newChallenge}
              >
                新的挑战
              </Button>
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
}

export default CodeBattle; 